import type { ProductContext } from "~/ui-rtk/app/slices/ad-forge.slice"

/**
 * Renders Mega Feed product facts into the short digest the AI compose and
 * motion prompts expect.
 *
 * Deliberately terse and capped. This string is prepended to every generation
 * prompt, so it competes for attention with the creative direction — dumping a
 * 1,500-word product description in here buries the actual instruction. Order
 * runs most- to least- identifying, so the truncation drops the least useful
 * material first.
 *
 * Returns "" when there is no product attached, which lets callers fall back to
 * their existing URL-scraped digest with a plain `||`.
 */
export function buildProductContextDigest(
  context: ProductContext | undefined,
  maxLength = 500,
): string {
  if (!context) return ""

  const audience = [context.gender, context.ageGroup].filter(Boolean).join(" ")
  const price =
    context.price && context.priceCurrency
      ? `${context.price} ${context.priceCurrency}`
      : context.price

  const parts = [
    context.name,
    context.brand,
    context.productType ?? context.googleProductCategory,
    audience || undefined,
    // The LLM one-liner is the single most prompt-usable field: it says what
    // the product IS in one sentence, which is exactly what the model needs.
    context.headerDescription,
    price,
    context.specs,
    // Long-form copy last — it is the first thing the cap should drop.
    context.description,
  ]

  return parts
    .map(part => (part ?? "").trim())
    .filter(part => part.length > 0)
    .join(" — ")
    .slice(0, maxLength)
}

/**
 * Tighter variant for motion prompts. Veo prompts are short and shot-focused,
 * so a long product paragraph actively degrades the result — this keeps only
 * enough to identify the subject.
 */
export function buildMotionProductContext(
  context: ProductContext | undefined,
): string {
  if (!context) return ""

  return [context.name, context.brand, context.headerDescription]
    .map(part => (part ?? "").trim())
    .filter(part => part.length > 0)
    .join(" — ")
    .slice(0, 200)
}

/**
 * Renders the company Brand Kit into a short prompt line so generated motion
 * matches the brand system rather than defaulting to generic stock styling.
 *
 * Colours and type only — the kit's logo is an overlay concern, baked onto the
 * frame afterwards, not something the video model should try to draw.
 */
export function buildBrandContext(
  brandKit:
    | {
        colors?: {
          primary?: string
          secondary?: string
          accent?: string
          background?: string
        }
        fonts?: { heading?: string; body?: string }
      }
    | undefined,
): string {
  if (!brandKit) return ""

  const palette = [
    brandKit.colors?.primary,
    brandKit.colors?.secondary,
    brandKit.colors?.accent,
    brandKit.colors?.background,
  ]
    .filter(Boolean)
    .join(", ")

  const type = [brandKit.fonts?.heading, brandKit.fonts?.body]
    .filter(Boolean)
    .join(" / ")

  return [
    palette ? `brand palette ${palette}` : "",
    type ? `brand typefaces ${type}` : "",
  ]
    .filter(Boolean)
    .join("; ")
    .slice(0, 200)
}
